import { PageController } from '@client/components/_core/layout/PageController';
import { EntityListBody } from '@client/components/_shared/tables/EntityListBody';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ControlFilters } from '@client/components/controls/ControlFilters';
import { CONTROL_LIST_STALE_ERROR, controlListColumns } from '@client/components/controls/control-columns';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

const ControlSearchSchema = z.object({
    'control-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.coerce.bigint(),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('handling-party-ids'),
                    operator: FilterOperatorSchema,
                    value: z.array(z.coerce.bigint()),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('type'),
                    operator: FilterOperatorSchema,
                    value: z.string(),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/controls/register/')({
    staticData: {
        ui: {
            title: 'Control register',
            subtitle: 'Manage and track your risk controls',
        },
    },
    validateSearch: zodValidator(ControlSearchSchema),
    component: ControlsRegisterSearch,
});

type QueryParams = RouterInputs['control']['listControls'];

function ControlsRegisterSearch() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        where: {},
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isPending, error, refetch, isFetching } = trpc.control.listControls.useQuery(input, {
        meta: { errorSurface: 'inline' },
    });

    const columns = controlListColumns(orgId, 'register');

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'control-register-table-columns',
            enableReordering: true,
        },
        emptyState: { noRecordsTitle: 'No controls yet' },
    });

    return (
        <PageController route={Route}>
            <EntityListBody
                table={tableProps}
                error={error}
                blockingError={hasBlockingQueryError(error, data?.rows)}
                onRetry={() => void refetch()}
                retrying={isFetching}
                staleError={CONTROL_LIST_STALE_ERROR}
                filters={
                    <ControlFilters
                        initialFilters={searchParams['control-filters']}
                        onChange={(where) => setInput((prev) => ({ ...prev, where }))}
                    />
                }
                toolbarExtra={
                    <Button.Add.Link
                        label="Add control"
                        to="/console/$orgId/controls/register/add"
                        params={{ orgId }}
                        variant="primary"
                    />
                }
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick: (e) =>
                        navigate({ to: '/console/$orgId/controls/register/details/$id', params: { orgId, id } }, e),
                })}
            />
        </PageController>
    );
}
